import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useEffect, useRef, useMemo } from 'react';
import cn from 'classnames';
import './TfGridLoading.less';
/**
 * 6x6 grid loading component with diagonal animations.
 * Used as React Suspense fallback and anywhere in-app loading is needed.
 * index.html uses a static grid + CSS-only pulse for the initial splash; only the grid
 * pattern (G) and palette colors need to stay in sync if you change them here.
 * Uses direct DOM manipulation to avoid React re-renders for high performance.
 */ var N = 6 // Grid size
;
// Initial pattern (0: Background, 1: Pink, 2: Dark Gray)
var G = [
    [
        0,
        0,
        2,
        2,
        0,
        0
    ],
    [
        0,
        0,
        2,
        2,
        0,
        0
    ],
    [
        1,
        1,
        1,
        1,
        2,
        2
    ],
    [
        1,
        1,
        1,
        1,
        2,
        2
    ],
    [
        0,
        0,
        1,
        1,
        0,
        0
    ],
    [
        0,
        0,
        1,
        1,
        0,
        0
    ]
];
// [Set Index, Is Left Direction]
var MODES = [
    [
        0,
        true
    ],
    [
        1,
        false
    ],
    [
        0,
        false
    ],
    [
        1,
        true
    ]
];
var TfGridLoading = function TfGridLoading(param) {
    var _param_size = param.size, size = _param_size === void 0 ? 8.1 : _param_size, _param_bgColor = param.bgColor, bgColor = _param_bgColor === void 0 ? 'transparent' : _param_bgColor, _param_isDark = param.isDark, isDark = _param_isDark === void 0 ? false : _param_isDark, _param_showLogo = param.showLogo, showLogo = _param_showLogo === void 0 ? false : _param_showLogo;
    var palette = useMemo(function() {
        return [
            bgColor,
            '#DA335E',
            isDark ? '#ECEDF4' : '#34343A'
        ];
    }, [
        bgColor,
        isDark
    ]);
    var gridRef = useRef(null);
    var cellsRef = useRef([]);
    var gap = size / 6;
    // Precompute diagonal groups to save CPU during animation
    var SETS = useMemo(function() {
        var antiDiags = Array.from({
            length: 2 * N - 1
        }, function(_, d) {
            var a = [];
            for(var r = Math.max(0, d - N + 1); r <= Math.min(d, N - 1); r++)a.push([
                r,
                d - r
            ]);
            return a;
        });
        var mainDiags = Array.from({
            length: 2 * N - 1
        }, function(_, d) {
            var k = d - N + 1;
            var a = [];
            for(var r = Math.max(0, k); r <= Math.min(N - 1, k + N - 1); r++)a.push([
                r,
                r - k
            ]);
            return a;
        });
        return [
            antiDiags,
            mainDiags
        ];
    }, []);
    useEffect(function() {
        var step = 0;
        var cycle = 0;
        var diags;
        var colors;
        var dir;
        var timeoutId;
        var isMounted = true;
        // Initialize direction and color data for new cycle
        var initCycle = function initCycle() {
            var m = cycle % 4;
            diags = SETS[MODES[m][0]];
            dir = MODES[m][1];
            colors = diags.map(function(d) {
                return d.map(function(param) {
                    var _param = _sliced_to_array(param, 2), r = _param[0], c = _param[1];
                    return G[r][c];
                });
            });
        };
        // Main animation tick
        var tick = function tick1() {
            if (!isMounted) return;
            var HALF = 8 // Steps in one direction
            ;
            var TOTAL = HALF * 2;
            var MIN = 20 // Min frame delay (ms)
            ;
            var MAX = 200 // Max frame delay (ms)
            ;
            var PAUSE = 500 // Pause between cycles (ms)
            ;
            if (step === 0) initCycle();
            // Shift colors in array
            var goLeft = step < HALF === dir;
            var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
            try {
                for(var _iterator = colors[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                    var cArr = _step.value;
                    if (goLeft) {
                        var first = cArr.shift();
                        if (first !== undefined) cArr.push(first);
                    } else {
                        var last = cArr.pop();
                        if (last !== undefined) cArr.unshift(last);
                    }
                }
            } catch (err) {
                _didIteratorError = true;
                _iteratorError = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion && _iterator.return != null) {
                        _iterator.return();
                    }
                } finally{
                    if (_didIteratorError) {
                        throw _iteratorError;
                    }
                }
            }
            step = (step + 1) % TOTAL;
            var dist = HALF - Math.abs(step - HALF);
            var stagger = (HALF - dist) * 16;
            // Update cell styles directly via Refs
            for(var k = 0; k < diags.length; k++){
                var delay = k / (diags.length - 1) * stagger;
                for(var i = 0; i < diags[k].length; i++){
                    var _diags_k_i = _sliced_to_array(diags[k][i], 2), r = _diags_k_i[0], c = _diags_k_i[1];
                    var ci = colors[k][i];
                    var cell = cellsRef.current[r] ? cellsRef.current[r][c] : null;
                    if (cell) {
                        cell.style.transitionDelay = stagger ? "".concat(delay, "ms") : '0ms';
                        cell.style.backgroundColor = palette[ci];
                        cell.style.opacity = ci === 0 ? (1 - dist * 0.1).toString() : '1';
                    }
                }
            }
            // Add slight 'breathing' scale effect to grid
            if (gridRef.current) {
                gridRef.current.style.transform = "scale(".concat(1 + dist * 0.006, ")");
            }
            // Handle pause and reset between animation cycles
            if (step === 0) {
                cycle++;
                var settle = stagger + 160;
                timeoutId = setTimeout(function() {
                    if (!isMounted) return;
                    for(var r = 0; r < N; r++){
                        for(var c = 0; c < N; c++){
                            var cell = cellsRef.current[r] ? cellsRef.current[r][c] : null;
                            if (cell) cell.style.transitionDelay = '0ms';
                        }
                    }
                    timeoutId = setTimeout(tick, PAUSE);
                }, settle);
                return;
            }
            // Easing speed using cosine
            var ease = 0.5 + 0.5 * Math.cos(2 * Math.PI * step / TOTAL);
            timeoutId = setTimeout(tick, MIN + (MAX - MIN) * ease);
        };
        timeoutId = setTimeout(tick, 200);
        return function() {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, [
        SETS,
        palette
    ]);
    if (cellsRef.current.length === 0) {
        cellsRef.current = Array.from({
            length: N
        }, function() {
            return Array(N).fill(null);
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('tf-grid-loading-wrapper', {
            'with-logo': showLogo
        }),
        children: [
            showLogo && /*#__PURE__*/ _jsx("img", {
                src: "/assets/logo-filled-small.png",
                className: "tf-loading-logo",
                alt: "Teamflect",
                style: isDark ? {
                    filter: 'brightness(3) grayscale(1)'
                } : undefined
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "tf-grid-loading-grid",
                ref: gridRef,
                style: {
                    gridTemplateColumns: "repeat(".concat(N, ", ").concat(size, "px)"),
                    gridTemplateRows: "repeat(".concat(N, ", ").concat(size, "px)"),
                    gap: "".concat(gap, "px")
                },
                children: Array.from({
                    length: N
                }).map(function(_, r) {
                    return Array.from({
                        length: N
                    }).map(function(_, c) {
                        var isTopLeft = r === 0 && c === 0;
                        var isTopRight = r === 0 && c === N - 1;
                        var isBottomLeft = r === N - 1 && c === 0;
                        var isBottomRight = r === N - 1 && c === N - 1;
                        return /*#__PURE__*/ _jsx("div", {
                            ref: function ref(el) {
                                if (cellsRef.current[r]) {
                                    cellsRef.current[r][c] = el;
                                }
                            },
                            className: "tf-grid-loading-grid-cell",
                            style: {
                                width: "".concat(size, "px"),
                                height: "".concat(size, "px"),
                                backgroundColor: palette[G[r][c]],
                                borderTopLeftRadius: isTopLeft ? '50%' : '25%',
                                borderTopRightRadius: isTopRight ? '50%' : '25%',
                                borderBottomLeftRadius: isBottomLeft ? '50%' : '25%',
                                borderBottomRightRadius: isBottomRight ? '50%' : '25%',
                                transition: 'background-color 160ms, opacity 160ms'
                            }
                        }, "".concat(r, "-").concat(c));
                    });
                })
            })
        ]
    });
};
export default TfGridLoading;
